import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import ChefCoinIcon from "@/components/ui/ChefCoinIcon";

interface BottomActionBarProps {
  onBack?: () => void;
  onNext?: () => void;
  backLabel?: string;
  nextLabel?: string;
  nextDisabled?: boolean;
  isLoading?: boolean;
  showCost?: number;
  hideOnDesktop?: boolean;
}

const BottomActionBar: React.FC<BottomActionBarProps> = ({
  onBack,
  onNext,
  backLabel = "Back",
  nextLabel = "Next",
  nextDisabled = false,
  isLoading = false,
  showCost,
  hideOnDesktop = false,
}) => {
  return (
    <>
      {/* Mobile: Fixed bottom bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white/95 glass-ios-strong border-t border-slate-200 px-3 py-2.5 pb-[max(env(safe-area-inset-bottom),0.5rem)]"
      >
        <div className="flex gap-2 max-w-lg mx-auto">
          {onBack && (
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={isLoading}
              className="h-11 px-4 text-sm font-medium text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {backLabel}
            </Button>
          )}
          {onNext && (
            <Button
              type="button"
              onClick={onNext}
              disabled={nextDisabled || isLoading}
              className={`flex-1 h-11 px-4 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-md shadow-orange-500/20 ${!onBack ? 'w-full' : ''}`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  <span>Generating…</span>
                </span>
              ) : showCost != null ? (
                <span className="flex items-center justify-center gap-1.5">
                  <Sparkles className="h-4 w-4" />
                  <span>{nextLabel}</span>
                  <span className="mx-1 h-4 w-px bg-white/30" />
                  <ChefCoinIcon className="h-4 w-4" />
                  <span className="font-bold tabular-nums">{showCost}</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1.5">
                  <span>{nextLabel}</span>
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Desktop: Inline buttons (rendered by parent, this just provides spacing) */}
      {!hideOnDesktop && (
        <div className="hidden md:flex justify-between gap-4 mt-8">
          {onBack && (
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={isLoading}
              className="h-11 px-6 font-medium text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {backLabel}
            </Button>
          )}
          {onNext && (
            <Button
              type="button"
              onClick={onNext}
              disabled={nextDisabled || isLoading}
              className={`h-11 px-8 font-semibold text-white bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-md shadow-orange-500/20 focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 ${onBack ? '' : 'w-full max-w-xs mx-auto'}`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  <span>Generating…</span>
                </span>
              ) : showCost != null ? (
                <span className="flex items-center justify-center gap-1.5">
                  <Sparkles className="h-4 w-4" />
                  <span>{nextLabel}</span>
                  <span className="mx-1 h-4 w-px bg-white/30" />
                  <ChefCoinIcon className="h-5 w-5" />
                  <span className="font-bold tabular-nums">{showCost}</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1.5">
                  <span>{nextLabel}</span>
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default BottomActionBar;
